/**
 * Case-closure predicates, shared by the board grouping (which decides whether a
 * row sits in "Delivered" or the collapsed "Closed" group) and by the status
 * editors that trigger the paperwork-complete notification.
 *
 * These live in their own module rather than in `EnrichedFuneralGroup` because
 * the status-cell editors import them, and `EnrichedFuneralGroup` imports those
 * editors — colocating would be an import cycle.
 */

/**
 * Death benefits are "settled" — nothing left for us to do on the case.
 *
 * Mirrors `benefitsSettled` in the backend's `case-state/process-definition.ts`;
 * keep the two in step. Mailing the CPP packet to Service Canada is the last
 * action we control, so 'sent' counts even though `death_benefits_status` stays
 * 'not_completed' until an agent records a confirmation number. Ineligible =
 * nothing to file.
 */
export function benefitsSettled(record: any): boolean {
  return (
    record?.death_benefits_status === 'completed' ||
    record?.death_benefits_status === 'ineligible' ||
    record?.cpp_benefits_mailing_status === 'sent'
  );
}

/** Every paperwork cell green. Registry is QC-only — Ontario has no registry. */
export function paperworkGreen(record: any): boolean {
  const decOk = !!record?.dec_ready;
  const attestationOk = !!record?.attestation_ready;
  const registryOk =
    record?.region === 'ontario' ? true : !!record?.preneed_registry;
  return decOk && benefitsSettled(record) && attestationOk && registryOk;
}

/**
 * Forward-only cutoff for the confirmation-email requirement below.
 *
 * Deliberately a FIXED date, not a rolling window: a rolling window would let a
 * case that's been forgotten for long enough quietly slip back into "Closed" —
 * reintroducing the exact bug this guard exists to prevent. Cases delivered
 * before this date predate the rule and stay closed, rather than resurfacing a
 * year of backlog (~320 cases) on the board.
 */
export const PAPERWORK_EMAIL_ENFORCED_FROM = '2026-07-14';

/**
 * The family is still owed the paperwork-complete confirmation email.
 *
 * Quebec-only — it carries the DEC and benefits confirmation numbers, which
 * Ontario cases don't have. Matches the `regions: ['quebec']` gate on the
 * `paperwork_complete_notification` step in both process definitions.
 */
export function awaitingPaperworkConfirmationEmail(record: any): boolean {
  if (String(record?.region || '').toLowerCase() !== 'quebec') return false;
  if (record?.paperwork_complete_email_sent_at) return false;
  // "Close without email" — staff decided writing the family isn't appropriate.
  if (record?.paperwork_email_waived_at) return false;
  if (!paperworkGreen(record)) return false;
  const deliveredAt = record?.delivered_at;
  if (!deliveredAt) return false;
  return new Date(deliveredAt) >= new Date(PAPERWORK_EMAIL_ENFORCED_FROM);
}

/**
 * Closed = delivered, every paperwork cell green, AND the family has had their
 * confirmation email (or it was explicitly waived).
 *
 * The email is the last step and it used to be the easiest to lose: completing
 * the final paperwork toggle moved the row straight into the collapsed "Closed"
 * group at the bottom of the board, out of sight. Holding the case in
 * "Delivered" until the email is resolved makes forgetting it structurally
 * impossible instead of merely discouraged.
 */
export function isClosed(record: any): boolean {
  if (record?.cremation_status !== 'delivered') return false;
  if (!paperworkGreen(record)) return false;
  return !awaitingPaperworkConfirmationEmail(record);
}
